import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { ComponentProps, FC, PropsWithChildren, SVGProps } from 'react'

interface DropdownSelectProps extends PropsWithChildren<ComponentProps<typeof Select>> {}

function DropdownSelect(props: DropdownSelectProps) {
  return <Select {...props} />
}

interface DropdownSelectTriggerProps
  extends PropsWithChildren<ComponentProps<typeof SelectTrigger>> {}

function DropdownSelectTrigger({ children, ...props }: DropdownSelectTriggerProps) {
  return (
    <SelectTrigger
      className="bg-vive-primary-70/50 hover:bg-vive-primary-40/50 h-[58px] border-none px-4 py-2 backdrop-blur-[50px] transition-all md:h-18 md:min-w-full md:px-4 md:py-3"
      {...props}
    >
      {children}
    </SelectTrigger>
  )
}

interface DropdownSelectValueProps extends PropsWithChildren<ComponentProps<typeof SelectValue>> {
  title: string
  description: string
  Icon?: FC<SVGProps<SVGElement>>
}

function DropdownSelectValue({ title, description, Icon, ...props }: DropdownSelectValueProps) {
  return (
    <SelectValue asChild {...props}>
      <div className="flex flex-row items-center gap-4">
        {Icon && <Icon className="fill-vive-primary-30 h-5 w-5" />}
        <div className="text-vive-primary-30 flex flex-col justify-center text-left">
          <div className="subheader-3 text-[14px] md:text-[16px]">{title}</div>
          <div className="body-2 text-[12px] md:text-[14px]">{description}</div>
        </div>
      </div>
    </SelectValue>
  )
}

interface DropdownSelectContentProps
  extends PropsWithChildren<ComponentProps<typeof SelectContent>> {}

function DropdownSelectContent({ children, className, ...props }: DropdownSelectContentProps) {
  return (
    <SelectContent
      className={cn(
        'bg-vive-primary-70/50 w-full gap-1 border-none backdrop-blur-[50px] [&>div]:flex [&>div]:flex-col md:[&>div]:gap-2',
        className,
      )}
      {...props}
    >
      {children}
    </SelectContent>
  )
}

export interface DropdownSelectItemProps
  extends PropsWithChildren<ComponentProps<typeof SelectItem>> {
  value: string
  title: string
  description: string
  selected?: boolean
  Icon?: FC<SVGProps<SVGElement>>
}

function DropdownSelectItem({
  title,
  description,
  className,
  Icon,
  selected,
  ...props
}: DropdownSelectItemProps) {
  return (
    <SelectItem
      className={cn(
        'focus:hover:bg-vive-primary-40/50 w-full cursor-pointer rounded-[3px] px-4 py-3 focus:hover:backdrop-blur-[50px]',
        className,
      )}
      withChecked={false}
      {...props}
    >
      <div className="flex flex-row items-center gap-4">
        {Icon && (
          <Icon className={cn('fill-vive-primary-30 h-5 w-5', selected && 'fill-vive-brand-40')} />
        )}
        <div className="text-vive-primary-30 flex flex-col justify-center text-left">
          <div
            className={cn(
              'subheader-3 text-[14px] md:text-[16px]',
              selected && 'text-vive-brand-40',
            )}
          >
            {title}
          </div>
          <div
            className={cn('body-2 text-[12px] md:text-[14px]', selected && 'text-vive-brand-40')}
          >
            {description}
          </div>
        </div>
      </div>
    </SelectItem>
  )
}

DropdownSelect.Trigger = DropdownSelectTrigger
DropdownSelect.Value = DropdownSelectValue
DropdownSelect.Content = DropdownSelectContent
DropdownSelect.Item = DropdownSelectItem

export default DropdownSelect
